import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { api, patchKatNode, flattenKatTree, stylySpizirny } from '../lib/spizirnaUi';
import {
    Warehouse, Plus, Edit3, Trash2, X, Loader2, Save,
    Package, BoxSelect, Star, Eye, ShoppingBag, ChevronRight,
} from 'lucide-react';
import MaterialListPanel from '../components/spizirna/MaterialListPanel';
import SurovinyListPanel from '../components/spizirna/SurovinyListPanel';
import ModulLayout from '../components/layout/ModulLayout';
import MaterialDetailHeader from '../components/spizirna/MaterialDetailHeader';
import PouzitiModal from '../components/receptura/PouzitiModal';
import MaterialStatistika from '../components/spizirna/MaterialStatistika';
import Cenik from '../components/spizirna/Cenik';
import CenaModal from '../components/spizirna/CenaModal';
import JednotkyModal from '../components/receptura/JednotkyModal';

const SECTION_COLOR = '#F59E0B';
const SECTION_BG = 'rgba(245,158,11,0.12)';

const fmtDate = (v) => { if (!v) return ''; const d = new Date(v); return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`; };

// Obaly a spotřební zboží sdílejí jednu záložku: obojí je nákup, který jde do
// kalkulace stejně, a obsluha mezi tím rozlišuje až u konkrétní karty. Uvnitř
// zůstávají dvě tabulky a dva ceníky, takže seznam nese u položky druh.
const TABS = [
    { id: 'suroviny', label: 'Suroviny', Icon: Package },
    { id: 'obaly', label: 'Obaly', Icon: BoxSelect },
    { id: 'spotrebni', label: 'Provozní materiál', Icon: ShoppingBag },
];

export default function MaterialoveKarty({
    onNavigate, initialPolozka = null, initialDodavatelId = null,
    // Vložená do modulu (Receptář / Balírna): záložku i hlavičku řídí obal. Bez těch
    // propů si stránka vystačí sama, takže jde pořád otevřít samostatně.
    tab: tabZvenku = null, onTab = null, vlozena = false, sekceBarva = null,
    // Kolik místa nechat pod obsahem scrollujících ploch (mobil: Palec obalu).
    // Nedává se jako odsazení celého modulu — ten jde až ke spodní hraně,
    // jinak by pod ním zůstal prázdný pruh a modul by zase vypadal orámovaně.
    rezervaDole = null,
}) {
    const { theme } = useTheme();
    const isDark = theme === 'dark' ||
        (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    const [tabVlastni, setTabVlastni] = useState('suroviny');
    const tab = tabZvenku ?? tabVlastni;
    const setTab = onTab ?? setTabVlastni;
    // Proklik z karty dodavatele — seznam se zúží na jeho sortiment.
    const [dodFiltr, setDodFiltr] = useState(null);   // { id, nazev } | null
    // Kde se položka používá (+ hromadná náhrada) — { typ, id } | null
    const SEKCE = sekceBarva || SECTION_COLOR;
    const [pouzitiModal, setPouzitiModal] = useState(null);
    // Široký displej = statistika jako stálý třetí sloupec, jako kalkulace
    // u receptury. Pod 1280 px by se nevešla, tak se schová.
    const [isWide, setIsWide] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1280);
    useEffect(() => {
        const onResize = () => setIsWide(window.innerWidth >= 1280);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    // Správa jednotek položky (základní + sekundární) — 'MAT' | 'OBA' | 'SPOZ' | null
    const [jednotkyModal, setJednotkyModal] = useState(null);
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    const {
        secondaryColor, mutedColor, cardStyle, inputStyle, selectStyle, optionStyle,
        btnPrimary, btnSecondary, thStyle, tdStyle, renderFormField,
    } = stylySpizirny(isDark);


    // ─── Shared data (ciselniky) ─────────────────────────────
    const [jednotky, setJednotky] = useState([]);
    const [kategorie, setKategorie] = useState([]);
    const [alergenyList, setAlergenyList] = useState([]);
    const [dphSazby, setDphSazby] = useState([]);

    useEffect(() => {
        api('/api/ciselniky/jednotky').then(setJednotky).catch(() => {});
        api('/api/ciselniky/kategorie-surovin').then(setKategorie).catch(() => {});
        api('/api/ciselniky/alergeny').then(setAlergenyList).catch(() => {});
        api('/api/ciselniky/dph-sazby').then(setDphSazby).catch(() => {});
    }, []);

    // ─── Tree helpers ────────────────────────────────────────
    // ─── Tab: Suroviny ──────────────────────────────────────
    const [suroviny, setSuroviny] = useState([]);
    const [surSearch, setSurSearch] = useState('');
    const [surLoading, setSurLoading] = useState(false);
    const [surSaving, setSurSaving] = useState(false);
    const [surDetail, setSurDetail] = useState(null); // selected surovina id or 'new'
    const [surDetailData, setSurDetailData] = useState(null);
    const [surEditData, setSurEditData] = useState(null); // editable copy for inline form
    const [surDetailTab, setSurDetailTab] = useState('alergeny');
    const [matKatStrom, setMatKatStrom] = useState([]);
    const [surAlergeny, setSurAlergeny] = useState([]);
    const [surBezAlergenu, setSurBezAlergenu] = useState(false);
    const [surCeny, setSurCeny] = useState([]);
    const [surAltJednotky, setSurAltJednotky] = useState([]);
    const [cenaModal, setCenaModal] = useState(null);   // null = zavřeno, {} = nová cena, jinak úprava
    const [cenaChyba, setCenaChyba] = useState('');
    const [cenaSaving, setCenaSaving] = useState(false);
    const [surError, setSurError] = useState('');

    const loadSuroviny = useCallback(async () => {
        setSurLoading(true);
        try { setSuroviny(await api('/api/recepty/suroviny')); } catch {}
        setSurLoading(false);
    }, []);

    const loadSurDetail = async (id) => {
        setSurDetail(id);
        // První viditelná záložka: na mobilu Karta (hlavička už pole nenese),
        // na desktopu Alergeny (identitu tam drží hlavička).
        setSurDetailTab(isMobile ? 'karta' : 'alergeny');
        try {
            const d = await api(`/api/recepty/suroviny/${id}`);
            setSurDetailData(d);
            setSurEditData({ nazev: d.nazev, jednotka_id: d.jednotka_id, kategorie_id: d.kategorie_id || '', aktivni: d.aktivni, poznamka: d.poznamka || '' });
            setSurAlergeny((d.alergeny || []).map(a => a.id));
            setSurBezAlergenu(!!d.bez_alergenu);
            setSurCeny(d.ceny || []);
            setSurAltJednotky(d.alt_jednotky || []);
        } catch {}
    };

    // Alergeny mají vlastní endpoint, ale ne vlastní tlačítko — ukládá je Uložit
    // v hlavičce spolu se zbytkem karty. Dvě tlačítka na jedné kartě znamenala,
    // že zaškrtnutý alergen odešel jen tehdy, když si obsluha všimla toho
    // druhého; „uložil jsem" přitom platilo jen na půlku změn.
    const alergenyDirty = (() => {
        if (surDetail === 'new' || !surDetailData) return false;
        if (surBezAlergenu !== !!surDetailData.bez_alergenu) return true;
        const puvodni = (surDetailData.alergeny || []).map(a => a.id).sort((a, b) => a - b);
        const ted = (surBezAlergenu ? [] : surAlergeny).slice().sort((a, b) => a - b);
        return puvodni.length !== ted.length || puvodni.some((v, i) => v !== ted[i]);
    })();

    const saveSurovina = async () => {
        setSurSaving(true); setSurError('');
        try {
            if (surDetail === 'new') {
                const created = await api('/api/recepty/suroviny', 'POST', surEditData);
                await loadSuroviny();
                loadSurDetail(created.id);
            } else {
                await api(`/api/recepty/suroviny/${surDetail}`, 'PUT', surEditData);
                // Alergeny až po hlavičce a jen když se opravdu změnily — ať se
                // na každé uložení nevolá endpoint, který nemá co zapsat.
                if (alergenyDirty) {
                    await api(`/api/recepty/suroviny/${surDetail}/alergeny`, 'PUT', {
                        alergen_ids: surBezAlergenu ? [] : surAlergeny,
                        bez_alergenu: surBezAlergenu,
                    });
                }
                await loadSuroviny();
                loadSurDetail(surDetail);
            }
        } catch (e) { setSurError(e.message || e.error || 'Chyba při ukládání'); }
        setSurSaving(false);
    };

    const deleteSurovina = async (id) => {
        if (!confirm('Opravdu smazat surovinu?')) return;
        try {
            await api(`/api/recepty/suroviny/${id}`, 'DELETE');
            setSurDetail(null); setSurDetailData(null); setSurEditData(null);
            loadSuroviny();
        } catch (e) { alert(e.error || 'Nelze smazat'); }
    };

    // Ceník se ukládá rovnou (je to vlastní záznam, ne pole karty) — formulář
    // k tomu ale stojí v modalu, ať zadávání nezasouvá ceník mimo obrazovku.
    const saveCena = async (data) => {
        setCenaSaving(true); setCenaChyba('');
        try {
            if (data.id) {
                await api(`/api/recepty/ceny/${data.id}`, 'PUT', data);
            } else {
                await api(`/api/recepty/suroviny/${surDetail}/ceny`, 'POST', data);
            }
            setSurCeny(await api(`/api/recepty/suroviny/${surDetail}/ceny`));
            setCenaModal(null);
            loadSuroviny();                      // seznam nese aktuální cenu ve sloupci
        } catch (e) { setCenaChyba(e.error || e.message || 'Chyba při ukládání ceny'); }
        setCenaSaving(false);
    };

    // Smazání i přehození primárního mění cenu, kterou seznam ukazuje ve sloupci
    // (i semafor „Bez ceny"), takže se přenačítá i on — jinak by řádek vlevo
    // tvrdil něco jiného než karta vpravo, dokud se seznam sám nenačte.
    const deleteCena = async (id) => {
        if (!confirm('Smazat cenu?')) return;
        try {
            await api(`/api/recepty/ceny/${id}`, 'DELETE');
            setSurCeny(await api(`/api/recepty/suroviny/${surDetail}/ceny`));
            loadSuroviny();
        } catch {}
    };

    const togglePrimarni = async (cenaId) => {
        try {
            await api(`/api/recepty/ceny/${cenaId}/primarni`, 'PUT');
            setSurCeny(await api(`/api/recepty/suroviny/${surDetail}/ceny`));
            loadSuroviny();
        } catch {}
    };

    // ─── Tab: Obaly ────────────────────────────────────────
    const [obaly, setObaly] = useState([]);
    const [obalSearch, setObalSearch] = useState('');
    const [obalLoading, setObalLoading] = useState(false);
    const [obalSaving, setObalSaving] = useState(false);
    const [obalDetail, setObalDetail] = useState(null);
    const [obalDetailData, setObalDetailData] = useState(null);
    const [obalEditData, setObalEditData] = useState(null);
    const [obalDetailTab, setObalDetailTab] = useState('info');
    const [obaKatStrom, setObaKatStrom] = useState([]);
    const [obalCeny, setObalCeny] = useState([]);
    const [obalAltJednotky, setObalAltJednotky] = useState([]);
    const [obalCenaForm, setObalCenaForm] = useState(null);
    const [obalCenaSaving, setObalCenaSaving] = useState(false);
    const [obalError, setObalError] = useState('');
    const [prohlaseniList, setProhlaseniList] = useState([]);

    const loadObaly = useCallback(async () => {
        setObalLoading(true);
        try { setObaly(await api('/api/recepty/obaly')); } catch {}
        setObalLoading(false);
    }, []);

    const loadObalDetail = async (id) => {
        // Sloučená záložka má jeden pravý panel — otevřít obal znamená zavřít zboží.
        setSpozDetail(null); setSpozDetailData(null); setSpozEditData(null);
        setObalDetail(id);
        setObalDetailTab('info');
        try {
            const d = await api(`/api/recepty/obaly/${id}`);
            setObalDetailData(d);
            setObalEditData({
                nazev: d.nazev, jednotka_id: d.jednotka_id, kategorie_id: d.kategorie_id || '',
                smernice_id: d.smernice_id || '', vyska_mm: d.vyska_mm || '', sirka_mm: d.sirka_mm || '',
                hloubka_mm: d.hloubka_mm || '', hmotnost_g: d.hmotnost_g || '',
                aktivni: d.aktivni, poznamka: d.poznamka || '',
            });
            setObalCeny(d.ceny || []);
            setObalAltJednotky(d.alt_jednotky || []);
        } catch {}
    };

    const saveObal = async () => {
        setObalSaving(true); setObalError('');
        try {
            if (obalDetail === 'new') {
                const created = await api('/api/recepty/obaly', 'POST', obalEditData);
                await loadObaly();
                loadObalDetail(created.id);
            } else {
                await api(`/api/recepty/obaly/${obalDetail}`, 'PUT', obalEditData);
                await loadObaly();
                loadObalDetail(obalDetail);
            }
        } catch (e) { setObalError(e.message || e.error || 'Chyba při ukládání'); }
        setObalSaving(false);
    };

    const deleteObal = async (id) => {
        if (!confirm('Opravdu smazat obal?')) return;
        try {
            await api(`/api/recepty/obaly/${id}`, 'DELETE');
            setObalDetail(null); setObalDetailData(null); setObalEditData(null);
            loadObaly();
        } catch (e) { alert(e.error || 'Nelze smazat'); }
    };

    const saveObalCena = async () => {
        setObalCenaSaving(true);
        try {
            if (obalCenaForm.id) {
                await api(`/api/recepty/ceny-obalu/${obalCenaForm.id}`, 'PUT', obalCenaForm);
            } else {
                await api(`/api/recepty/obaly/${obalDetail}/ceny`, 'POST', obalCenaForm);
            }
            setObalCenaForm(null);
            setObalCeny(await api(`/api/recepty/obaly/${obalDetail}/ceny`));
        } catch (e) { alert(e.error || 'Chyba'); }
        setObalCenaSaving(false);
    };

    const deleteObalCena = async (id) => {
        if (!confirm('Smazat cenu?')) return;
        try {
            await api(`/api/recepty/ceny-obalu/${id}`, 'DELETE');
            setObalCeny(await api(`/api/recepty/obaly/${obalDetail}/ceny`));
        } catch {}
    };

    const toggleObalPrimarni = async (cenaId) => {
        try {
            await api(`/api/recepty/ceny-obalu/${cenaId}/primarni`, 'PUT');
            setObalCeny(await api(`/api/recepty/obaly/${obalDetail}/ceny`));
        } catch {}
    };

    // ─── Tab: Spotřební zboží ──────────────────────────────
    const [spoz, setSpoz] = useState([]);
    const [spozLoading, setSpozLoading] = useState(false);
    const [spozSaving, setSpozSaving] = useState(false);
    const [spozDetail, setSpozDetail] = useState(null);
    const [spozDetailData, setSpozDetailData] = useState(null);
    const [spozEditData, setSpozEditData] = useState(null);
    const [spozDetailTab, setSpozDetailTab] = useState('info');
    const [spozKatStrom, setSpozKatStrom] = useState([]);
    const [spozCeny, setSpozCeny] = useState([]);
    const [spozAltJednotky, setSpozAltJednotky] = useState([]);
    const [spozCenaForm, setSpozCenaForm] = useState(null);
    const [spozCenaSaving, setSpozCenaSaving] = useState(false);
    const [spozError, setSpozError] = useState('');

    const loadSpoz = useCallback(async () => {
        setSpozLoading(true);
        try { setSpoz(await api('/api/recepty/spotrebni-zbozi')); } catch {}
        setSpozLoading(false);
    }, []);

    const loadSpozDetail = async (id) => {
        setObalDetail(null); setObalDetailData(null); setObalEditData(null);
        setSpozDetail(id); setSpozDetailTab('info');
        try {
            const d = await api(`/api/recepty/spotrebni-zbozi/${id}`);
            setSpozDetailData(d);
            setSpozEditData({
                nazev: d.nazev, jednotka_id: d.jednotka_id, kategorie_id: d.kategorie_id || '',
                dodavatel_id: d.dodavatel_id || '', aktivni: d.aktivni, poznamka: d.poznamka || '',
            });
            setSpozCeny(d.ceny || []);
            setSpozAltJednotky(d.alt_jednotky || []);
        } catch {}
    };

    const saveSpoz = async () => {
        setSpozSaving(true); setSpozError('');
        try {
            if (spozDetail === 'new') {
                const created = await api('/api/recepty/spotrebni-zbozi', 'POST', spozEditData);
                await loadSpoz(); loadSpozDetail(created.id);
            } else {
                await api(`/api/recepty/spotrebni-zbozi/${spozDetail}`, 'PUT', spozEditData);
                await loadSpoz(); loadSpozDetail(spozDetail);
            }
        } catch (e) { setSpozError(e.message || e.error || 'Chyba při ukládání'); }
        setSpozSaving(false);
    };

    const deleteSpoz = async (id) => {
        if (!confirm('Opravdu smazat spotřební zboží?')) return;
        try {
            await api(`/api/recepty/spotrebni-zbozi/${id}`, 'DELETE');
            setSpozDetail(null); setSpozDetailData(null); setSpozEditData(null);
            loadSpoz();
        } catch (e) { alert(e.error || 'Nelze smazat'); }
    };

    const saveSpozCena = async () => {
        setSpozCenaSaving(true);
        try {
            if (spozCenaForm.id) {
                await api(`/api/recepty/ceny-spotrebni-zbozi/${spozCenaForm.id}`, 'PUT', spozCenaForm);
            } else {
                await api(`/api/recepty/spotrebni-zbozi/${spozDetail}/ceny`, 'POST', spozCenaForm);
            }
            setSpozCenaForm(null);
            setSpozCeny(await api(`/api/recepty/spotrebni-zbozi/${spozDetail}/ceny`));
        } catch (e) { alert(e.error || 'Chyba'); }
        setSpozCenaSaving(false);
    };

    const deleteSpozCena = async (id) => {
        if (!confirm('Smazat cenu?')) return;
        try {
            await api(`/api/recepty/ceny-spotrebni-zbozi/${id}`, 'DELETE');
            setSpozCeny(await api(`/api/recepty/spotrebni-zbozi/${spozDetail}/ceny`));
        } catch {}
    };

    const toggleSpozPrimarni = async (cenaId) => {
        try {
            await api(`/api/recepty/ceny-spotrebni-zbozi/${cenaId}/primarni`, 'PUT');
            setSpozCeny(await api(`/api/recepty/spotrebni-zbozi/${spozDetail}/ceny`));
        } catch {}
    };

    // ─── Dodavatelé ────────────────────────────────────────
    // Jen čtená nabídka do výběru u ceníků. Karta dodavatele (kontakty, štítky,
    // kalendář závozů, statistiky) se spravuje ve Financích → Dodavatelé.
    const [dodavatele, setDodavatele] = useState([]);
    const loadDodavatele = useCallback(async () => {
        try { setDodavatele(await api('/api/dodavatele')); } catch {}
    }, []);

    // lazy tab loading
    useEffect(() => {
        if (tab === 'suroviny') {
            if (suroviny.length === 0) loadSuroviny();
            loadDodavatele();
            api('/api/recepty/kategorie-strom?typ=MAT').then(setMatKatStrom).catch(() => {});
        }
        if (tab === 'obaly') {
            if (obaly.length === 0) loadObaly();
            if (spoz.length === 0) loadSpoz();
            loadDodavatele();
            api('/api/recepty/kategorie-strom?typ=OBA').then(setObaKatStrom).catch(() => {});
            api('/api/recepty/kategorie-strom?typ=SPOZ').then(setSpozKatStrom).catch(() => {});
            api('/api/recepty/prohlaseni-o-shode').then(setProhlaseniList).catch(() => {});
        }
    }, [tab]);

    // ─── Prokliky zvenčí ───────────────────────────────────
    // Z receptáře „otevři tuhle surovinu", z karty dodavatele „ukaž jeho sortiment".
    const TAB_PRO_TYP = { MAT: 'suroviny', OBA: 'obaly', SPOZ: 'obaly' };

    useEffect(() => {
        const cilovyTab = TAB_PRO_TYP[initialPolozka?.typ];
        if (!cilovyTab) return;
        setTab(cilovyTab);
        // Bez ID jde jen o přepnutí sekce (proklik „co od něj bereme") — filtr
        // podle dodavatele musí zůstat. S ID otevíráme konkrétní kartu.
        if (!initialPolozka.id) return;
        setDodFiltr(null);
        if (initialPolozka.typ === 'MAT') loadSurDetail(initialPolozka.id);
        if (initialPolozka.typ === 'OBA') loadObalDetail(initialPolozka.id);
        if (initialPolozka.typ === 'SPOZ') loadSpozDetail(initialPolozka.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialPolozka?.typ, initialPolozka?.id]);

    useEffect(() => {
        if (!initialDodavatelId) return;
        // Jméno doplníme, jakmile dojede seznam dodavatelů (do té doby jen ID).
        const d = dodavatele.find(x => x.id === initialDodavatelId);
        setDodFiltr({ id: initialDodavatelId, nazev: d?.nazev || `#${initialDodavatelId}` });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialDodavatelId, dodavatele.length]);

    // Položka patří dodavateli, když od něj má aspoň jednu cenu. Seznamový
    // endpoint ceník nevrací (jen `dodavatele_ids`) — dřív se tu četlo `ceny`,
    // které v seznamu nejsou, a filtr nepropustil nic.
    const odDodavatele = (x) => !dodFiltr || (x.dodavatele_ids ?? (x.ceny || []).map(c => c.dodavatel_id)).includes(dodFiltr.id);

    // Odkaz na kartu dodavatele ve Financích — z ceníku rovnou na protistranu.
    const OdkazNaDodavatele = ({ cena }) => {
        if (!cena?.dodavatel?.nazev) return <span>-</span>;
        if (!onNavigate || !cena.dodavatel_id) return <span>{cena.dodavatel.nazev}</span>;
        return (
            <button
                onClick={() => onNavigate('finance', { tab: 'dodavatele', dodavatelId: cena.dodavatel_id })}
                title="Otevřít kartu dodavatele ve Financích"
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit', font: 'inherit', textAlign: 'left' }}
                onMouseEnter={e => { e.currentTarget.style.color = SECTION_COLOR; e.currentTarget.style.textDecoration = 'underline'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'inherit'; e.currentTarget.style.textDecoration = 'none'; }}>
                {cena.dodavatel.nazev}
            </button>
        );
    };

    // ─── Neuložené změny v hlavičce detailu ─────────────────
    // Hlavička nese název, zařazení, jednotku a aktivitu; tlačítko Uložit podle
    // toho svítí tečkou a zůstává neaktivní, dokud se nic nezměnilo.
    const poleDirty = (detail, data, edit) => (key) => {
        if (detail === 'new') return false;      // u nové položky nemá co blikat
        if (!data || !edit) return false;
        if (key === 'aktivni') return (edit.aktivni ?? true) !== (data.aktivni ?? true);
        return (edit[key] ?? '') !== (data[key] ?? '');
    };
    const jeDirty = (detail, data, edit, pole) => {
        if (detail === 'new') return true;
        const f = poleDirty(detail, data, edit);
        return pole.some(f);
    };
    const HLAVICKA_POLE = ['nazev', 'kategorie_id', 'jednotka_id', 'aktivni', 'poznamka'];

    const surPoleDirty = poleDirty(surDetail, surDetailData, surEditData);
    // Alergeny nemají vlastní tlačítko, takže musí rozsvítit to v hlavičce —
    // jinak by Uložit zůstalo šedé a guard by pustil obsluhu pryč beze změn.
    const surDirty = jeDirty(surDetail, surDetailData, surEditData, HLAVICKA_POLE) || alergenyDirty;
    // Jako guardReceptura v receptáři: výběr jiné suroviny, klik na kategorii
    // (návrat do přehledu) i šipky nesmí tiše zahodit rozepsané změny. U nové
    // suroviny se hlídá až rozepsaný název — prázdný formulář nemá co ztratit.
    const guardSurovina = () => {
        const neulozeno = surDetail === 'new' ? !!surEditData?.nazev?.trim() : surDirty;
        if (!neulozeno) return true;
        return window.confirm('U suroviny máš neuložené změny. Opustit bez uložení? Změny se ztratí.');
    };
    const zavritSurovinu = () => { setSurDetail(null); setSurDetailData(null); setSurEditData(null); };
    const novaSurovina = () => {
        setSurDetail('new'); setSurDetailData(null);
        setSurEditData({ nazev: '', jednotka_id: jednotky[0]?.id || '', kategorie_id: '', aktivni: true, poznamka: '' });
        setSurDetailTab(isMobile ? 'karta' : 'alergeny'); setSurError('');
    };

    const obalPoleDirty = poleDirty(obalDetail, obalDetailData, obalEditData);
    const obalDirty = jeDirty(obalDetail, obalDetailData, obalEditData,
        [...HLAVICKA_POLE, 'smernice_id', 'vyska_mm', 'sirka_mm', 'hloubka_mm', 'hmotnost_g']);
    const spozPoleDirty = poleDirty(spozDetail, spozDetailData, spozEditData);
    const spozDirty = jeDirty(spozDetail, spozDetailData, spozEditData, [...HLAVICKA_POLE, 'dodavatel_id']);

    // ─── Kategorie a hromadné přesuny ───────────────────────
    // Číselník kategorií je společný s receptářem (stg_kategorie_surovin, jen
    // jiný `typ`), takže i obsluha stromu je stejná — jen se liší, který strom
    // a který seznam se po změně přenačte.
    const nactiKatStrom = useCallback((typ) => {
        const setStrom = typ === 'MAT' ? setMatKatStrom : typ === 'OBA' ? setObaKatStrom : setSpozKatStrom;
        api(`/api/recepty/kategorie-strom?typ=${typ}`).then(setStrom).catch(() => {});
    }, []);

    const katHandlery = (typ, setStrom, prenacti) => ({
        // Optimisticky: strom se přepíše hned, PUT běží na pozadí.
        onCatSave: (id, patch) => {
            setStrom(prev => patchKatNode(prev, id, patch));
            api(`/api/recepty/kategorie-surovin/${id}`, 'PUT', patch)
                .catch(e => { alert(e.error || 'Chyba'); nactiKatStrom(typ); });
        },
        onCatAdd: (parentId, data) => api('/api/recepty/kategorie-surovin', 'POST', { typ, parent_id: parentId, aktivni: true, ...data })
            .then(() => nactiKatStrom(typ)).catch(e => alert(e.error || 'Chyba')),
        onCatDelete: (id) => api(`/api/recepty/kategorie-surovin/${id}`, 'DELETE')
            .then(() => { nactiKatStrom(typ); prenacti(); }).catch(e => alert(e.error || 'Chyba')),
        onReparentCat: (catId, parentId) => api(`/api/recepty/kategorie-surovin/${catId}`, 'PUT', { parent_id: parentId })
            .then(() => nactiKatStrom(typ)).catch(e => alert(e.error || 'Přesun kategorie se nezdařil')),
    });

    // Přeřazení položek do kategorie přetažením (i celého výběru). Položky nemají
    // PATCH endpoint, takže se posílá pětice polí, kterou umí všechny tři typy;
    // ostatní sloupce (rozměry obalu apod.) zůstanou beze změny.
    const presunDoKategorie = (ids, catId, { seznam, setSeznam, endpoint, flatKat, prenacti, detailId, nactiDetail }) => {
        const k = flatKat.find(x => x.id === catId);
        const kat = k ? { id: k.id, nazev: k.nazev, barva: k.barva, ikona: k.ikona } : null;
        const menene = ids.map(id => seznam.find(x => x.id === id)).filter(x => x && x.kategorie_id !== catId);
        if (menene.length === 0) return;

        setSeznam(prev => prev.map(x => (menene.some(m => m.id === x.id) ? { ...x, kategorie_id: catId, kategorie: kat } : x)));

        Promise.all(menene.map(x => api(`${endpoint}/${x.id}`, 'PUT', {
            nazev: x.nazev, jednotka_id: x.jednotka_id, kategorie_id: catId,
            aktivni: x.aktivni, poznamka: x.poznamka ?? '',
        })))
            .then(() => { if (menene.some(m => m.id === detailId)) nactiDetail(detailId); })
            .catch(e => { alert(e.error || 'Přesun se nezdařil'); prenacti(); });
    };

    // ─── RENDER data ────────────────────────────────────────
    const flatMatKat = useMemo(() => flattenKatTree(matKatStrom), [matKatStrom]);
    const flatObaKat = useMemo(() => flattenKatTree(obaKatStrom), [obaKatStrom]);
    const flatSpozKat = useMemo(() => flattenKatTree(spozKatStrom), [spozKatStrom]);

    // Kategorie i archiv řeší strom v panelu — tady zbývá jen hledání.
    // Slouží už jen k navigaci šipkami, proto stejné řazení jako v seznamu.
    const hledej = (q) => (x) => !q || x.nazev?.toLowerCase().includes(q.toLowerCase()) || x.kod?.toLowerCase().includes(q.toLowerCase());
    const filteredSur = suroviny.filter(hledej(surSearch));

    // Vlastní hledání pro každý druh — dva seznamy, dvě políčka.
    const [spozSearch, setSpozSearch] = useState('');
    const filteredObaly = obaly.filter(hledej(obalSearch)).filter(odDodavatele);
    const filteredSpoz = spoz.filter(hledej(spozSearch)).filter(odDodavatele);

    const novyObal = () => {
        setSpozDetail(null); setSpozDetailData(null); setSpozEditData(null);
        setObalDetail('new'); setObalDetailData(null);
        setObalEditData({ nazev: '', jednotka_id: jednotky[0]?.id || '', kategorie_id: '', smernice_id: '', vyska_mm: '', sirka_mm: '', hloubka_mm: '', hmotnost_g: '', aktivni: true, poznamka: '' });
        setObalDetailTab('info'); setObalError('');
    };
    const noveSpoz = () => {
        setObalDetail(null); setObalDetailData(null); setObalEditData(null);
        setSpozDetail('new'); setSpozDetailData(null);
        setSpozEditData({ nazev: '', jednotka_id: jednotky[0]?.id || '', kategorie_id: '', dodavatel_id: '', aktivni: true, poznamka: '' });
        setSpozDetailTab('info'); setSpozError('');
    };

    // ─── Keyboard navigace šipkami ↑↓ v detailu ────────────
    useEffect(() => {
        const handler = (e) => {
            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
            e.preventDefault();

            const dir = e.key === 'ArrowUp' ? -1 : 1;
            let nextId = null;

            if (tab === 'suroviny' && surDetail && surDetail !== 'new') {
                const idx = filteredSur.findIndex(s => s.id === surDetail);
                const next = idx + dir;
                if (next >= 0 && next < filteredSur.length && guardSurovina()) { nextId = filteredSur[next].id; loadSurDetail(nextId); }
            } else if (tab === 'obaly' && obalDetail && obalDetail !== 'new') {
                const idx = filteredObaly.findIndex(x => x.id === obalDetail);
                const next = idx + dir;
                if (next >= 0 && next < filteredObaly.length) { nextId = filteredObaly[next].id; loadObalDetail(nextId); }
            } else if (tab === 'spotrebni' && spozDetail && spozDetail !== 'new') {
                const idx = filteredSpoz.findIndex(x => x.id === spozDetail);
                const next = idx + dir;
                if (next >= 0 && next < filteredSpoz.length) { nextId = filteredSpoz[next].id; loadSpozDetail(nextId); }
            }

            // Scroll aktivní položku do view
            if (nextId) {
                setTimeout(() => {
                    document.querySelector(`[data-list-id="${nextId}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                }, 50);
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [tab, surDetail, obalDetail, spozDetail, filteredSur, filteredObaly, filteredSpoz]);

    return (
        // `flex: 1` vedle `height: 100%`: obal v Receptáři je flex sloupec (tam
        // roste flexem), obal v Balírně obyčejný blok (tam platí výška). Bez toho
        // stojí plná výška na tom, že se procento prořeže přes flexový prvek — a
        // když se neprořeže, zkrátí se karta na výšku obsahu a pod ní zůstane pruh.
        <div style={vlozena ? { display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0, overflow: 'hidden' } : undefined}>
            {/* Header — na mobilu se zobrazuje v TopBaru, tady skryjeme.
                Ve vloženém režimu ho kreslí obal, jinak by byly dva. */}
            {!isMobile && !vlozena && (
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: SECTION_BG }}>
                        <Warehouse size={20} style={{ color: SECTION_COLOR }} />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Karty materiálu</h1>
                        <p className="text-xs" style={{ color: secondaryColor }}>
                            Karty, ceníky a zařazení — hlavičku jinak kreslí Receptář nebo Balírna
                        </p>
                    </div>
                </div>
            )}

            {/* Tab bar — ve vloženém režimu ho nese obal se všemi sedmi záložkami */}
            {!vlozena && (
            <div className="flex gap-1 mb-6" style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}` }}>
                {TABS.map(t => (
                    <button key={t.id} onClick={() => { setTab(t.id); setJednotkyModal(null); }}
                        className="flex items-center px-4 py-2 text-sm font-medium transition-colors"
                        style={{ color: tab === t.id ? SECTION_COLOR : secondaryColor, background: 'none', border: 'none',
                            borderBottom: `2px solid ${tab === t.id ? SECTION_COLOR : 'transparent'}`, cursor: 'pointer' }}>
                        <t.Icon size={14} style={{ marginRight: 6, opacity: tab === t.id ? 1 : 0.6 }} />
                        {t.label}
                    </button>
                ))}
            </div>
            )}

            {/* Filtr podle dodavatele — proklik z jeho karty ve Financích. U surovin
                ho nese filtr v hlavičce seznamu (čip „Dodavatel: …"). */}
            {dodFiltr && tab !== 'suroviny' && (
                <div className="flex items-center gap-2 mb-4 -mt-3">
                    <span className="text-xs" style={{ color: secondaryColor }}>Sortiment dodavatele:</span>
                    <button onClick={() => setDodFiltr(null)}
                        title="Zrušit filtr"
                        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold"
                        style={{ background: SECTION_BG, color: SECTION_COLOR, border: `1px solid ${SECTION_COLOR}55`, cursor: 'pointer' }}>
                        {dodFiltr.nazev}
                        <X size={12} />
                    </button>
                    {onNavigate && (
                        <button onClick={() => onNavigate('finance', { tab: 'dodavatele', dodavatelId: dodFiltr.id })}
                            className="text-xs" style={{ background: 'none', border: 'none', cursor: 'pointer', color: secondaryColor, textDecoration: 'underline' }}>
                            zpět na kartu dodavatele
                        </button>
                    )}
                </div>
            )}

            {/* ═══════ TAB: SUROVINY ═══════
                 Standardní rozvržení modulu (components/layout/ModulLayout, norma
                 standard-rozvrzeni.md) stejně jako polotovary a produkty: bez
                 výběru je seznam přehled přes celou šířku, po výběru se zúží,
                 vedle je detail a stálý panel se statistikou ceny a použití. */}
            {tab === 'suroviny' && (
                <div style={{
                    display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden',
                    ...(vlozena ? { flex: 1 } : { height: 'calc(100vh - 200px)' }),
                }}>
                <ModulLayout
                    isMobile={isMobile}
                    vybrano={!!surDetail}
                    /* Mobil: seznam přes celou obrazovku → detail. Třetí sloupec
                       (statistika) se na telefonu nekreslí vůbec — `mobilPanel={false}`.
                       Je to karta „co k položce jen informuje", tedy přesně to, co se
                       na malém displeji čte nejmíň, a stálo to úchyt u pravé hrany,
                       gesto, které si musel každý najít, a zásuvku, která parkovala
                       za hranou a rozšiřovala scrollovatelnou plochu modulu.
                       Na desktopu panel zůstává. */
                    mobilRezim
                    mobilPanel={false}
                    mobilRezervaDole={rezervaDole}
                    barva={SEKCE} isDark={isDark}
                    seznam={({ siroky }) => (
                        <SurovinyListPanel siroky={siroky} kompaktni={isMobile} rezervaDole={isMobile ? rezervaDole : null}
                            isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor}
                            cardStyle={cardStyle} inputStyle={inputStyle}
                            search={surSearch} setSearch={setSurSearch}
                            katStrom={matKatStrom} items={suroviny} loading={surLoading}
                            activeId={surDetail}
                            onSelect={(id) => { if (guardSurovina()) loadSurDetail(id); }}
                            onNew={() => { if (guardSurovina()) novaSurovina(); }}
                            onKategorieKlik={() => {
                                if (!surDetail) return 'nic';                  // už jsme v přehledu → obyčejný toggle
                                if (!guardSurovina()) return 'blokovano';      // neuložené změny drží detail otevřený
                                zavritSurovinu();
                                return 'prehled';
                            }}
                            dodavatele={dodavatele} alergenyList={alergenyList}
                            dodavatelZvenku={dodFiltr?.id ?? null}
                            onMoveItems={(ids, catId) => presunDoKategorie(ids, catId, {
                                seznam: suroviny, setSeznam: setSuroviny, endpoint: '/api/recepty/suroviny',
                                flatKat: flatMatKat, prenacti: loadSuroviny, detailId: surDetail, nactiDetail: loadSurDetail,
                            })}
                            {...katHandlery('MAT', setMatKatStrom, loadSuroviny)}
                        />
                    )}
                    detail={() => (
                            /* Na mobilu je detail rovnou plocha displeje, ne karta —
                               bez pozadí, průhledně na pozadí aplikace (viz SurovinyListPanel). */
                            <div className={`overflow-hidden flex flex-col flex-1 ${isMobile ? '' : 'rounded-xl'}`}
                                style={isMobile ? { minHeight: 0 } : { ...cardStyle, minHeight: 0 }}>
                                <MaterialDetailHeader
                                    detail={surDetail} data={surDetailData} edit={surEditData} setEdit={setSurEditData}
                                    flatKat={flatMatKat} jednotky={jednotky} novyLabel="Název suroviny"
                                    dirty={surDirty} saving={surSaving} onSave={saveSurovina} onDelete={() => deleteSurovina(surDetail)}
                                    onBack={() => { if (guardSurovina()) zavritSurovinu(); }}
                                    onOpenJednotky={() => setJednotkyModal('MAT')} altJednotkyPocet={surAltJednotky.length}
                                    poleDirty={surPoleDirty} maPoznamku={!isMobile} jenNavigace
                                    isDark={isDark} isMobile={isMobile} mutedColor={mutedColor} secondaryColor={secondaryColor}
                                    optionStyle={optionStyle} btnPrimary={btnPrimary} btnSecondary={btnSecondary} />

                                {/* Chyba ukládání stála v „Základní info" — po zrušení té
                                    záložky by se schovala za tu zrovna otevřenou. Patří
                                    pod hlavičku, kde je Uložit, které ji vyvolalo. */}
                                {surError && (
                                    <p className="text-xs" style={{ color: '#EF4444', padding: isMobile ? '8px 12px 0' : '8px 16px 0', flexShrink: 0 }}>{surError}</p>
                                )}

                                <JednotkyModal
                                    open={jednotkyModal === 'MAT' && !!surEditData} onClose={() => setJednotkyModal(null)}
                                    isDark={isDark} jednotky={jednotky}
                                    jednotkaId={surEditData?.jednotka_id || ''}
                                    onJednotkaChange={v => setSurEditData({ ...surEditData, jednotka_id: v })}
                                    altJednotky={surAltJednotky}
                                    onAltJednotkyChange={alt => {
                                        setSurAltJednotky(alt);
                                        setSurDetailData(prev => prev ? { ...prev, alt_jednotky: alt } : prev);
                                    }}
                                    api={api}
                                    listUrl={`/api/recepty/suroviny/${surDetail}/alt-jednotky`}
                                    itemUrl={id => `/api/recepty/alt-jednotky/${id}`}
                                    isNew={surDetail === 'new'} novaHint="Nejdříve uložte surovinu." />

                                {/* Sub-tabs — na mobilu se nezalamují, jedou v jedné přejeté řadě */}
                                <div className="flex gap-1 pt-3" style={{
                                    flexShrink: 0, paddingLeft: isMobile ? 12 : 16, paddingRight: isMobile ? 12 : 16,
                                    borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
                                    overflowX: 'auto', WebkitOverflowScrolling: 'touch',
                                }}>
                                    {/* „Základní info" zrušeno: nesla poznámku (ta je teď v hlavičce)
                                        a souhrn, který doslova opisoval stálý panel vpravo. */}
                                    {/* „Karta" je jen na mobilu — na desktopu tahle pole nese
                                        hlavička, kde je na ně místo. */}
                                    {[
                                        ...(isMobile ? [{ id: 'karta', label: 'Karta', dirty: surPoleDirty('nazev') || surPoleDirty('kategorie_id') || surPoleDirty('jednotka_id') || surPoleDirty('aktivni') || surPoleDirty('poznamka') }] : []),
                                        { id: 'alergeny', label: 'Alergeny', dirty: alergenyDirty },
                                        { id: 'ceny', label: 'Ceny & dodavatelé' },
                                    ].map(st => (
                                        <button key={st.id} onClick={() => setSurDetailTab(st.id)}
                                            className={`font-medium whitespace-nowrap ${isMobile ? 'px-4 text-[13px]' : 'px-3 py-2 text-xs'}`}
                                            style={{ color: surDetailTab === st.id ? SECTION_COLOR : mutedColor, background: 'none', border: 'none',
                                                borderBottom: `2px solid ${surDetailTab === st.id ? SECTION_COLOR : 'transparent'}`, cursor: 'pointer', marginBottom: -1, flexShrink: 0,
                                                display: 'flex', alignItems: 'center', gap: 5,
                                                ...(isMobile ? { minHeight: 44, WebkitTapHighlightColor: 'transparent' } : null) }}>
                                            {st.label}
                                            {/* Tečka říká, kde čeká neuložená změna — Uložit je v hlavičce,
                                                takže jinak není poznat, která záložka ho rozsvítila. */}
                                            {st.dirty && <span style={{ width: 7, height: 7, borderRadius: '50%', background: SECTION_COLOR, flexShrink: 0 }} />}
                                        </button>
                                    ))}
                                </div>

                                {/* Sub-tab content — dole rezerva, ať konec obsahu
                                    jde vyscrollovat nad Palec */}
                                <div className="overflow-y-auto" style={{
                                    flex: 1, minHeight: 0, overscrollBehavior: 'contain',
                                    padding: isMobile ? 12 : 16,
                                    ...(rezervaDole ? { paddingBottom: `calc(${rezervaDole} + 12px)` } : null),
                                }}>
                                    {/* ─ KARTA TAB (jen mobil) ─
                                        Řádky v hustotě iOS nastavení: popisek vlevo, hodnota
                                        vpravo, celý řádek 44 px. Ovladače, které byly v hlavičce
                                        vysoké 18–28 px, tu mají plný cíl a hlavně popisek —
                                        zelený přepínač u Uložit dřív neřekl, co vlastně přepíná. */}
                                    {isMobile && surDetailTab === 'karta' && surEditData && (() => {
                                        const jemna = isDark ? 'rgba(255,255,255,0.07)' : 'var(--border-default)';
                                        const radek = (label, obsah, jeDirty) => (
                                            <div className="flex items-center gap-3" style={{
                                                minHeight: 44, borderBottom: `1px solid ${jemna}`,
                                                ...(jeDirty ? { boxShadow: 'inset 3px 0 0 rgba(245,158,11,0.75)', paddingLeft: 9 } : null),
                                            }}>
                                                <span className="text-[13px] flex-shrink-0" style={{ color: secondaryColor, width: 96 }}>{label}</span>
                                                <div className="flex-1 min-w-0 flex items-center justify-end gap-2">{obsah}</div>
                                            </div>
                                        );
                                        const hodnota = { fontSize: 14, color: 'var(--text-primary)' };
                                        return (
                                            <div>
                                                {radek('Název', (
                                                    <input value={surEditData.nazev || ''} onChange={e => setSurEditData({ ...surEditData, nazev: e.target.value })}
                                                        placeholder="Název suroviny"
                                                        style={{ ...hodnota, width: '100%', textAlign: 'right', background: 'transparent', border: 'none', outline: 'none', padding: '8px 0', fontWeight: 600 }} />
                                                ), surPoleDirty('nazev'))}

                                                {/* Vlastní nativní šipka pryč (`appearance: none`) — vysypala by se
                                                    doleva od zarovnané hodnoty doprostřed řádku. Ukazatel je stejný
                                                    „›" jako u Jednotky, ať se oba řádky čtou stejně. */}
                                                {radek('Kategorie', (
                                                    <>
                                                        <select value={surEditData.kategorie_id || ''} onChange={e => setSurEditData({ ...surEditData, kategorie_id: e.target.value ? parseInt(e.target.value) : null })}
                                                            style={{
                                                                ...hodnota, width: '100%', textAlign: 'right', background: 'transparent',
                                                                border: 'none', outline: 'none', padding: '8px 0', cursor: 'pointer',
                                                                appearance: 'none', WebkitAppearance: 'none',
                                                            }}>
                                                            <option value="" style={optionStyle}>Bez kategorie</option>
                                                            {flatMatKat.map(k => <option key={k.id} value={k.id} style={optionStyle}>{'— '.repeat(k._depth)}{k.nazev}</option>)}
                                                        </select>
                                                        <ChevronRight size={15} style={{ color: mutedColor, flexShrink: 0 }} />
                                                    </>
                                                ), surPoleDirty('kategorie_id'))}

                                                {radek('Jednotka', (
                                                    <button onClick={() => setJednotkyModal('MAT')}
                                                        className="flex items-center gap-1.5"
                                                        style={{ ...hodnota, background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0', color: surEditData.jednotka_id ? 'var(--text-primary)' : '#EF4444' }}>
                                                        {surEditData.jednotka_id ? (jednotky.find(j => j.id === surEditData.jednotka_id)?.zkratka || 'jedn.') : 'Vybrat *'}
                                                        {surAltJednotky.length > 0 && <span style={{ color: mutedColor }}>+{surAltJednotky.length}</span>}
                                                        <ChevronRight size={15} style={{ color: mutedColor }} />
                                                    </button>
                                                ), surPoleDirty('jednotka_id'))}

                                                {radek('Aktivní', (
                                                    <button onClick={() => setSurEditData({ ...surEditData, aktivni: !(surEditData.aktivni ?? true) })}
                                                        aria-pressed={surEditData.aktivni ?? true}
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0', display: 'flex', alignItems: 'center' }}>
                                                        <span style={{
                                                            width: 42, height: 26, borderRadius: 999, position: 'relative', transition: 'background 0.2s',
                                                            background: (surEditData.aktivni ?? true) ? '#22C55E' : (isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)'),
                                                        }}>
                                                            <span style={{ position: 'absolute', top: 3, left: (surEditData.aktivni ?? true) ? 19 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.35)' }} />
                                                        </span>
                                                    </button>
                                                ), surPoleDirty('aktivni'))}

                                                {/* Poznámka je jediné víceřádkové pole, takže dostane vlastní blok */}
                                                <div className="pt-3">
                                                    <label className="block text-[13px] mb-1.5" style={{ color: secondaryColor }}>Poznámka</label>
                                                    <textarea value={surEditData.poznamka || ''} onChange={e => setSurEditData({ ...surEditData, poznamka: e.target.value })}
                                                        rows={3} placeholder="—" style={{ ...inputStyle, ...(surPoleDirty('poznamka') ? { borderColor: SECTION_COLOR } : null) }} />
                                                </div>

                                            </div>
                                        );
                                    })()}

                                    {/* ─ ALERGENY TAB ─ */}
                                    {surDetailTab === 'alergeny' && surDetail !== 'new' && (
                                        <div>
                                            {/* Přepínač: Neobsahuje alergeny */}
                                            {/* Popisek jde na mobilu pod nadpis — vedle sebe by se
                                                „Neobsahuje alergeny" a celá věta zalomily do sloupce písmen. */}
                                            <label className={`flex gap-3 p-3 rounded-xl mb-4 cursor-pointer ${isMobile ? 'items-start' : 'items-center'}`} style={{
                                                background: surBezAlergenu ? 'rgba(34,197,94,0.12)' : 'transparent',
                                                border: `1px solid ${surBezAlergenu ? 'rgba(34,197,94,0.4)' : isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
                                                ...(isMobile ? { minHeight: 44 } : null),
                                            }}>
                                                <input type="checkbox" checked={surBezAlergenu}
                                                    style={isMobile ? { marginTop: 2, flexShrink: 0 } : null}
                                                    onChange={e => {
                                                        setSurBezAlergenu(e.target.checked);
                                                        if (e.target.checked) setSurAlergeny([]);
                                                    }} />
                                                <span className={isMobile ? 'min-w-0' : 'contents'}>
                                                    <span className="text-sm font-semibold" style={{ color: surBezAlergenu ? '#22C55E' : 'var(--text-primary)' }}>
                                                        Neobsahuje alergeny
                                                    </span>
                                                    <span className={`text-[11px] ${isMobile ? 'block mt-0.5' : ''}`} style={{ color: mutedColor }}>
                                                        {isMobile ? 'surovina byla zkontrolována a neobsahuje žádné alergeny'
                                                            : '— surovina byla zkontrolována a neobsahuje žádné alergeny'}
                                                    </span>
                                                </span>
                                            </label>

                                            {/* Jeden sloupec na mobilu: názvy alergenů jsou dlouhé
                                                („Obiloviny obsahující lepek"), ve dvou sloupcích po 170 px
                                                se lámou na tři řádky a seznam se nedá přelétnout. */}
                                            {!surBezAlergenu && (
                                                <div className="mb-4" style={{ columnCount: isMobile ? 1 : 2, columnGap: 8 }}>
                                                    {alergenyList.map(a => (
                                                        <label key={a.id} className={`flex items-center gap-2 rounded-lg text-xs mb-1 ${isMobile ? 'px-3' : 'p-2'}`} style={{ color: 'var(--text-primary)', cursor: 'pointer', breakInside: 'avoid',
                                                            background: surAlergeny.includes(a.id) ? SECTION_BG : 'transparent',
                                                            border: `1px solid ${surAlergeny.includes(a.id) ? SECTION_COLOR + '40' : isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
                                                            ...(isMobile ? { minHeight: 44, WebkitTapHighlightColor: 'transparent' } : null) }}>
                                                            <input type="checkbox" checked={surAlergeny.includes(a.id)}
                                                                onChange={e => { setSurAlergeny(e.target.checked ? [...surAlergeny, a.id] : surAlergeny.filter(x => x !== a.id)); }} />
                                                            <span className="font-semibold" style={{ color: SECTION_COLOR, minWidth: 18 }}>{a.cislo_eu}</span>
                                                            {a.nazev}
                                                        </label>
                                                    ))}
                                                </div>
                                            )}
                                            {/* Bez vlastního Uložit — alergeny odcházejí s kartou přes
                                                tlačítko v hlavičce (viz alergenyDirty). */}
                                        </div>
                                    )}
                                    {surDetailTab === 'alergeny' && surDetail === 'new' && (
                                        <p className="text-xs py-4" style={{ color: mutedColor }}>Nejdříve uložte surovinu.</p>
                                    )}

                                    {/* ─ CENY TAB ─ */}
                                    {surDetailTab === 'ceny' && surDetail !== 'new' && surDetailData && (
                                        <Cenik
                                            ceny={surCeny}
                                            jednotka={surDetailData.jednotka} jednotkaId={surDetailData.jednotka_id}
                                            onPridat={() => setCenaModal({})}
                                            onUpravit={(c) => setCenaModal(c)}
                                            onSmazat={deleteCena} onTogglePrimarni={togglePrimarni}
                                            OdkazNaDodavatele={OdkazNaDodavatele}
                                            isMobile={isMobile} isDark={isDark}
                                            mutedColor={mutedColor} secondaryColor={secondaryColor}
                                            btnPrimary={btnPrimary} />
                                    )}
                                    {surDetailTab === 'ceny' && surDetail === 'new' && (
                                        <p className="text-xs py-4" style={{ color: mutedColor }}>Nejdříve uložte surovinu.</p>
                                    )}
                                </div>

                                {/* Zadávání ceny v modalu — inline formulář odsouval ceník
                                    nahoru a na telefonu ho vytlačil mimo obrazovku. */}
                                <CenaModal
                                    cena={cenaModal} onClose={() => { setCenaModal(null); setCenaChyba(''); }}
                                    onSave={saveCena} saving={cenaSaving} chyba={cenaChyba}
                                    dodavatele={dodavatele} dphSazby={dphSazby}
                                    altJednotky={surAltJednotky}
                                    jednotka={surDetailData?.jednotka} jednotkaId={surDetailData?.jednotka_id}
                                    jePrvni={surCeny.length === 0}
                                    isDark={isDark} />
                            </div>
                    )}
                    /* Třetí sloupec je standardně otevřený — cena, její vývoj a kde
                       se surovina používá se čtou u každé karty. */
                    panel={() => surDetail !== 'new' && surDetailData ? (
                        <MaterialStatistika
                            typ="MAT" detail={surDetailData} ceny={surCeny}
                            isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor}
                            cardStyle={cardStyle} sekceBarva={SEKCE}
                            onPouziti={() => setPouzitiModal({ typ: 'MAT', id: surDetail })}
                        />
                    ) : null}
                />
                </div>
            )}

            {/* ═══════ TAB: OBALY A SPOTŘEBNÍ ZBOŽÍ ═══════ */}
            {tab === 'obaly' && (
                <div className={isMobile ? '' : 'flex gap-4'} style={{ height: vlozena ? '100%' : 'calc(100vh - 200px)', minHeight: 0, overflow: 'hidden' }}>
                    {/* ── Left panel: list ── */}
                    {(!isMobile || !obalDetail) && (
                    <MaterialListPanel
                        typ="OBA" labels={{ jednotne: 'obal', mnozne: 'obaly', novy: 'Nový obal' }}
                        isMobile={isMobile} isDark={isDark}
                        mutedColor={mutedColor} secondaryColor={secondaryColor}
                        cardStyle={cardStyle} inputStyle={inputStyle}
                        search={obalSearch} setSearch={setObalSearch}
                        katStrom={obaKatStrom} items={obaly} loading={obalLoading}
                        activeId={obalDetail}
                        onSelect={loadObalDetail}
                        onNew={novyObal}
                        onMoveItems={(ids, catId) => presunDoKategorie(ids, catId, {
                            seznam: obaly, setSeznam: setObaly, endpoint: '/api/recepty/obaly',
                            flatKat: flatObaKat, prenacti: loadObaly, detailId: obalDetail, nactiDetail: loadObalDetail,
                        })}
                        {...katHandlery('OBA', setObaKatStrom, loadObaly)}
                    />
                    )}

                    {/* ── Right panel: detail ── */}
                    <div className="flex-1 min-w-0 flex flex-col" style={{ display: isMobile && !obalDetail ? 'none' : 'flex' }}>
                        {!obalDetail ? (
                            <div className="rounded-xl flex items-center justify-center" style={{ ...cardStyle, height: '100%', minHeight: 400 }}>
                                <div className="text-center">
                                    <BoxSelect size={32} style={{ color: mutedColor, margin: '0 auto 12px' }} />
                                    <p className="text-sm" style={{ color: mutedColor }}>Vyberte obal ze seznamu</p>
                                    <p className="text-xs mt-1" style={{ color: mutedColor }}>nebo založte novou položku</p>
                                </div>
                            </div>
                        ) : (
                            <div className="rounded-xl overflow-hidden flex flex-col flex-1" style={cardStyle}>
                                <MaterialDetailHeader
                                    detail={obalDetail} data={obalDetailData} edit={obalEditData} setEdit={setObalEditData}
                                    flatKat={flatObaKat} jednotky={jednotky} novyLabel="Název obalu"
                                    dirty={obalDirty} saving={obalSaving} onSave={saveObal} onDelete={() => deleteObal(obalDetail)}
                                    onBack={() => { setObalDetail(null); setObalDetailData(null); setObalEditData(null); }}
                                    onOpenJednotky={() => setJednotkyModal('OBA')} altJednotkyPocet={obalAltJednotky.length}
                                    poleDirty={obalPoleDirty}
                                    isDark={isDark} isMobile={isMobile} mutedColor={mutedColor} secondaryColor={secondaryColor}
                                    optionStyle={optionStyle} btnPrimary={btnPrimary} btnSecondary={btnSecondary} />

                                <JednotkyModal
                                    open={jednotkyModal === 'OBA' && !!obalEditData} onClose={() => setJednotkyModal(null)}
                                    isDark={isDark} jednotky={jednotky}
                                    jednotkaId={obalEditData?.jednotka_id || ''}
                                    onJednotkaChange={v => setObalEditData({ ...obalEditData, jednotka_id: v })}
                                    altJednotky={obalAltJednotky}
                                    onAltJednotkyChange={alt => {
                                        setObalAltJednotky(alt);
                                        setObalDetailData(prev => prev ? { ...prev, alt_jednotky: alt } : prev);
                                    }}
                                    api={api}
                                    listUrl={`/api/recepty/obaly/${obalDetail}/alt-jednotky`}
                                    itemUrl={id => `/api/recepty/alt-jednotky-obalu/${id}`}
                                    isNew={obalDetail === 'new'} novaHint="Nejdříve uložte obal." />

                                <div className="flex gap-1 px-4 pt-3" style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                                    {[{ id: 'info', label: 'Základní info' }, { id: 'ceny', label: 'Ceny & dodavatelé' }].map(st => (
                                        <button key={st.id} onClick={() => setObalDetailTab(st.id)}
                                            className="px-3 py-2 text-xs font-medium"
                                            style={{ color: obalDetailTab === st.id ? SECTION_COLOR : mutedColor, background: 'none', border: 'none',
                                                borderBottom: `2px solid ${obalDetailTab === st.id ? SECTION_COLOR : 'transparent'}`, cursor: 'pointer', marginBottom: -1 }}>
                                            {st.label}
                                        </button>
                                    ))}
                                </div>

                                <div className="p-4 overflow-y-auto" style={{ flex: 1, minHeight: 0 }}>
                                    {/* ─ INFO TAB ─ */}
                                    {obalDetailTab === 'info' && obalEditData && (
                                        <div className="space-y-4">
                                            {/* Název, zařazení, jednotka i aktivita jsou v hlavičce. */}

                                            {/* Prohlášení o shodě */}
                                            {renderFormField('Prohlášení o shodě', <div className="flex gap-2">
                                                <select value={obalEditData.smernice_id || ''} onChange={e => setObalEditData({ ...obalEditData, smernice_id: e.target.value ? parseInt(e.target.value) : null })} style={{ ...selectStyle, flex: 1 }}>
                                                    <option value="" style={optionStyle}>— bez prohlášení —</option>
                                                    {prohlaseniList.map(p => <option key={p.id} value={p.id} style={optionStyle}>{p.nazev}</option>)}
                                                </select>
                                                {obalEditData.smernice_id && (
                                                    <button onClick={() => window.open(`/api/bezpecnost/smernice/${obalEditData.smernice_id}/download`, '_blank')}
                                                        style={{ ...btnSecondary, padding: '6px 10px', flexShrink: 0 }} title="Zobrazit dokument">
                                                        <Eye size={14} />
                                                    </button>
                                                )}
                                            </div>)}

                                            {/* Rozměry + hmotnost */}
                                            <div className="grid grid-cols-4 gap-4">
                                                {renderFormField('Výška (mm)', <input type="number" step="0.01" value={obalEditData.vyska_mm || ''} onChange={e => setObalEditData({ ...obalEditData, vyska_mm: e.target.value })} style={inputStyle} />)}
                                                {renderFormField('Šířka (mm)', <input type="number" step="0.01" value={obalEditData.sirka_mm || ''} onChange={e => setObalEditData({ ...obalEditData, sirka_mm: e.target.value })} style={inputStyle} />)}
                                                {renderFormField('Hloubka (mm)', <input type="number" step="0.01" value={obalEditData.hloubka_mm || ''} onChange={e => setObalEditData({ ...obalEditData, hloubka_mm: e.target.value })} style={inputStyle} />)}
                                                {renderFormField('Hmotnost (g)', <input type="number" step="0.01" value={obalEditData.hmotnost_g || ''} onChange={e => setObalEditData({ ...obalEditData, hmotnost_g: e.target.value })} style={inputStyle} />)}
                                            </div>

                                            {renderFormField('Poznámka', <textarea value={obalEditData.poznamka || ''} onChange={e => setObalEditData({ ...obalEditData, poznamka: e.target.value })} rows={3} style={inputStyle} />)}
                                            {obalError && <p className="text-xs" style={{ color: '#EF4444' }}>{obalError}</p>}

                                            {obalDetail !== 'new' && obalDetailData && (
                                                <div className="rounded-xl p-4 mt-2" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-base)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}` }}>
                                                    <p className="text-[11px] font-semibold uppercase tracking-wide mb-3" style={{ color: mutedColor }}>Přehled</p>
                                                    <div className="grid grid-cols-4 gap-4">
                                                        <div>
                                                            <p className="text-[11px]" style={{ color: mutedColor }}>Nákupní cena</p>
                                                            {(() => {
                                                                const pc = obalCeny.find(c => c.je_primarni && (!c.platnost_do || new Date(c.platnost_do) >= new Date()));
                                                                if (!pc) return <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>—</p>;
                                                                const isAlt = pc.cena_jednotka_id && pc.cena_jednotka_id !== obalDetailData.jednotka_id;
                                                                return <>
                                                                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                                                                        {parseFloat(pc.cena_za_jednotku).toFixed(2)} Kč/{obalDetailData.jednotka?.zkratka}
                                                                    </p>
                                                                    {isAlt && <p className="text-[10px]" style={{ color: mutedColor }}>zadáno {parseFloat(pc.cena_bez_dph).toFixed(2)} Kč/{pc.cena_jednotka?.zkratka}</p>}
                                                                </>;
                                                            })()}
                                                        </div>
                                                        <div>
                                                            <p className="text-[11px]" style={{ color: mutedColor }}>Dodavatel</p>
                                                            <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                                                                {(() => { const pc = obalCeny.find(c => c.je_primarni); return pc?.dodavatel?.nazev || '—'; })()}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <p className="text-[11px]" style={{ color: mutedColor }}>Sek. jednotky</p>
                                                            <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{obalAltJednotky.length}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-[11px]" style={{ color: mutedColor }}>V kusovnících</p>
                                                            {/* Klik ukáže, ve kterých recepturách — a nabídne hromadnou náhradu */}
                                                            <button
                                                                onClick={() => obalDetailData.kusovniky_jako_ingredience_count > 0 && setPouzitiModal({ typ: 'OBA', id: obalDetailData.id })}
                                                                disabled={!obalDetailData.kusovniky_jako_ingredience_count}
                                                                title={obalDetailData.kusovniky_jako_ingredience_count ? 'Zobrazit, kde se položka používá' : undefined}
                                                                className="text-sm font-semibold"
                                                                style={{
                                                                    background: 'none', border: 'none', padding: 0, textAlign: 'left',
                                                                    cursor: obalDetailData.kusovniky_jako_ingredience_count ? 'pointer' : 'default',
                                                                    color: obalDetailData.kusovniky_jako_ingredience_count > 0 ? SECTION_COLOR : 'var(--text-primary)',
                                                                    textDecoration: obalDetailData.kusovniky_jako_ingredience_count ? 'underline' : 'none',
                                                                }}>
                                                                {obalDetailData.kusovniky_jako_ingredience_count ?? 0}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* ─ CENY TAB ─ */}
                                    {obalDetailTab === 'ceny' && obalDetail !== 'new' && (
                                        <div>
                                            {obalCeny.length > 0 && (
                                                <div className="rounded-xl overflow-hidden mb-3" style={{ border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                                                    <table className="w-full">
                                                        <thead><tr style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                                                            <th style={{ ...thStyle, width: 28 }}></th><th style={thStyle}>Dodavatel</th><th style={thStyle}>Cena/{obalDetailData.jednotka?.zkratka}</th><th style={thStyle}>Zadáno</th><th style={thStyle}>DPH</th>
                                                            <th style={thStyle}>S DPH/{obalDetailData.jednotka?.zkratka}</th><th style={thStyle}>Od</th><th style={thStyle}>Do</th><th style={thStyle}></th>
                                                        </tr></thead>
                                                        <tbody>{obalCeny.map(c => {
                                                            const isAlt = c.cena_jednotka_id && c.cena_jednotka_id !== obalDetailData.jednotka_id;
                                                            return (
                                                            <tr key={c.id} style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'var(--border-default)'}`, fontWeight: c.je_primarni ? 600 : 400 }}>
                                                                <td style={tdStyle}>
                                                                    <button onClick={() => toggleObalPrimarni(c.id)} title={c.je_primarni ? 'Primární dodavatel' : 'Označit jako primární'}
                                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.je_primarni ? SECTION_COLOR : mutedColor, padding: 0 }}>
                                                                        <Star size={14} fill={c.je_primarni ? SECTION_COLOR : 'none'} />
                                                                    </button>
                                                                </td>
                                                                <td style={tdStyle}>
                                                                    <OdkazNaDodavatele cena={c} />
                                                                    {c.je_primarni && <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: SECTION_BG, color: SECTION_COLOR }}>prim.</span>}
                                                                </td>
                                                                <td style={tdStyle}>{parseFloat(c.cena_za_jednotku).toFixed(2)}</td>
                                                                <td style={{ ...tdStyle, fontSize: 11, color: isAlt ? secondaryColor : mutedColor }}>
                                                                    {isAlt ? `${parseFloat(c.cena_bez_dph).toFixed(2)}/${c.cena_jednotka?.zkratka}` : `/${obalDetailData.jednotka?.zkratka}`}
                                                                </td>
                                                                <td style={tdStyle}>{c.dph_sazba}%</td>
                                                                <td style={tdStyle}>{parseFloat(c.cena_s_dph_za_jednotku).toFixed(2)}</td>
                                                                <td style={tdStyle}>{fmtDate(c.platnost_od)}</td>
                                                                <td style={tdStyle}>{c.platnost_do ? fmtDate(c.platnost_do) : '—'}</td>
                                                                <td style={tdStyle}>
                                                                    <div className="flex gap-1">
                                                                        <button onClick={() => setObalCenaForm({ ...c, platnost_od: c.platnost_od?.split('T')[0], platnost_do: c.platnost_do?.split('T')[0] || '', je_primarni: c.je_primarni || false })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: secondaryColor }}><Edit3 size={13} /></button>
                                                                        <button onClick={() => deleteObalCena(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444' }}><Trash2 size={13} /></button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );})}</tbody>
                                                    </table>
                                                </div>
                                            )}
                                            {!obalCenaForm && (
                                                <button onClick={() => setObalCenaForm({ dodavatel_id: '', cena_bez_dph: '', dph_sazba: 21, cena_jednotka_id: null, platnost_od: new Date().toISOString().split('T')[0], platnost_do: '', je_primarni: obalCeny.length === 0 })} style={btnPrimary}>
                                                    <Plus size={14} /> Přidat cenu
                                                </button>
                                            )}
                                            {obalCenaForm && (
                                                <div className="rounded-xl p-4 space-y-3 mt-2" style={{ border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                                                    {renderFormField('Dodavatel *', <select value={obalCenaForm.dodavatel_id || ''} onChange={e => setObalCenaForm({ ...obalCenaForm, dodavatel_id: e.target.value ? parseInt(e.target.value) : null })} style={selectStyle}>
                                                        <option value="" style={optionStyle}>—</option>
                                                        {dodavatele.map(d => <option key={d.id} value={d.id} style={optionStyle}>{d.nazev}</option>)}
                                                    </select>)}
                                                    <div className={`grid gap-3 ${obalAltJednotky.length > 0 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                                                        {renderFormField('Cena bez DPH *', <input type="number" step="0.01" value={obalCenaForm.cena_bez_dph} onChange={e => setObalCenaForm({ ...obalCenaForm, cena_bez_dph: e.target.value })} style={inputStyle} />)}
                                                        {obalAltJednotky.length > 0 && renderFormField('Za jednotku', <select value={obalCenaForm.cena_jednotka_id || ''} onChange={e => setObalCenaForm({ ...obalCenaForm, cena_jednotka_id: e.target.value ? parseInt(e.target.value) : null })} style={selectStyle}>
                                                            <option value="" style={optionStyle}>{obalDetailData.jednotka?.zkratka} (základní)</option>
                                                            {obalAltJednotky.map(aj => <option key={aj.jednotka_do?.id} value={aj.jednotka_do?.id} style={optionStyle}>{aj.jednotka_do?.zkratka} (1 = {aj.koeficient} {obalDetailData.jednotka?.zkratka})</option>)}
                                                        </select>)}
                                                        {renderFormField('DPH', <select value={obalCenaForm.dph_sazba} onChange={e => setObalCenaForm({ ...obalCenaForm, dph_sazba: parseInt(e.target.value) })} style={selectStyle}>
                                                            {dphSazby.map(d => <option key={d.sazba} value={d.sazba} style={optionStyle}>{d.nazev}</option>)}
                                                        </select>)}
                                                    </div>
                                                    {(() => {
                                                        const selJed = obalCenaForm.cena_jednotka_id;
                                                        const isAlt = selJed && selJed !== obalDetailData.jednotka_id;
                                                        const alt = isAlt && obalAltJednotky.find(a => a.jednotka_do?.id === selJed);
                                                        const cenaBez = parseFloat(obalCenaForm.cena_bez_dph);
                                                        if (isAlt && alt && alt.koeficient > 0 && cenaBez > 0) {
                                                            const zaZakl = cenaBez / alt.koeficient;
                                                            const sDph = zaZakl * (1 + obalCenaForm.dph_sazba / 100);
                                                            return <p className="text-xs px-1 py-1.5 rounded" style={{ background: isDark ? 'rgba(245,158,11,0.08)' : 'rgba(245,158,11,0.06)', color: SECTION_COLOR }}>
                                                                = {zaZakl.toFixed(2)} Kč/{obalDetailData.jednotka?.zkratka} bez DPH | {sDph.toFixed(2)} Kč/{obalDetailData.jednotka?.zkratka} s DPH
                                                            </p>;
                                                        }
                                                        return null;
                                                    })()}
                                                    <div className="grid grid-cols-3 gap-3 items-end">
                                                        {renderFormField('Platnost od *', <input type="date" value={obalCenaForm.platnost_od || ''} onChange={e => setObalCenaForm({ ...obalCenaForm, platnost_od: e.target.value })} style={inputStyle} />)}
                                                        {renderFormField('Platnost do', <input type="date" value={obalCenaForm.platnost_do || ''} onChange={e => setObalCenaForm({ ...obalCenaForm, platnost_do: e.target.value || null })} style={inputStyle} />)}
                                                        <label className="flex items-center gap-2 text-xs pb-1" style={{ color: 'var(--text-primary)', cursor: 'pointer' }}>
                                                            <input type="checkbox" checked={obalCenaForm.je_primarni || false} onChange={e => setObalCenaForm({ ...obalCenaForm, je_primarni: e.target.checked })} />
                                                            <Star size={12} style={{ color: SECTION_COLOR }} /> Primární
                                                        </label>
                                                    </div>
                                                    <div className="flex justify-end gap-2">
                                                        <button onClick={() => setObalCenaForm(null)} style={btnSecondary}>Zrušit</button>
                                                        <button onClick={saveObalCena} style={btnPrimary} disabled={obalCenaSaving}>
                                                            {obalCenaSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Uložit
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {obalDetailTab === 'ceny' && obalDetail === 'new' && (
                                        <p className="text-xs py-4" style={{ color: mutedColor }}>Nejdříve uložte obal.</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── Statistika: cena, vývoj a kam vstupuje ── */}
                    {!isMobile && isWide && obalDetail && obalDetail !== 'new' && (
                        <div style={{ width: 300, flexShrink: 0, minHeight: 0, overflowY: 'auto' }}>
                            <MaterialStatistika
                                typ="OBA" detail={obalDetailData} ceny={obalCeny}
                                isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor}
                                cardStyle={cardStyle} sekceBarva={SEKCE}
                                onPouziti={() => setPouzitiModal({ typ: 'OBA', id: obalDetail })}
                            />
                        </div>
                    )}
                </div>
            )}

            {tab === 'spotrebni' && (
                <div className={isMobile ? '' : 'flex gap-4'} style={{ height: vlozena ? '100%' : 'calc(100vh - 200px)', minHeight: 0, overflow: 'hidden' }}>
                    {/* ── Left panel: list ── */}
                    {(!isMobile || !spozDetail) && (
                    <MaterialListPanel
                        typ="SPOZ" labels={{ jednotne: 'položku', mnozne: 'položky', novy: 'Nová položka' }}
                        isMobile={isMobile} isDark={isDark}
                        mutedColor={mutedColor} secondaryColor={secondaryColor}
                        cardStyle={cardStyle} inputStyle={inputStyle}
                        search={spozSearch} setSearch={setSpozSearch}
                        katStrom={spozKatStrom} items={spoz} loading={spozLoading}
                        activeId={spozDetail}
                        onSelect={loadSpozDetail}
                        onNew={noveSpoz}
                        onMoveItems={(ids, catId) => presunDoKategorie(ids, catId, {
                            seznam: spoz, setSeznam: setSpoz, endpoint: '/api/recepty/spotrebni-zbozi',
                            flatKat: flatSpozKat, prenacti: loadSpoz, detailId: spozDetail, nactiDetail: loadSpozDetail,
                        })}
                        {...katHandlery('SPOZ', setSpozKatStrom, loadSpoz)}
                    />
                    )}

                    {/* ── Right panel: detail ── */}
                    <div className="flex-1 min-w-0 flex flex-col" style={{ display: isMobile && !spozDetail ? 'none' : 'flex' }}>
                        {!spozDetail ? (
                            <div className="rounded-xl flex items-center justify-center" style={{ ...cardStyle, height: '100%', minHeight: 400 }}>
                                <div className="text-center">
                                    <BoxSelect size={32} style={{ color: mutedColor, margin: '0 auto 12px' }} />
                                    <p className="text-sm" style={{ color: mutedColor }}>Vyberte položku ze seznamu</p>
                                    <p className="text-xs mt-1" style={{ color: mutedColor }}>nebo založte novou položku</p>
                                </div>
                            </div>
                        ) : (
                            <div className="rounded-xl overflow-hidden flex flex-col flex-1" style={cardStyle}>
                                <MaterialDetailHeader
                                    detail={spozDetail} data={spozDetailData} edit={spozEditData} setEdit={setSpozEditData}
                                    flatKat={flatSpozKat} jednotky={jednotky} novyLabel="Název zboží"
                                    dirty={spozDirty} saving={spozSaving} onSave={saveSpoz} onDelete={() => deleteSpoz(spozDetail)}
                                    onBack={() => { setSpozDetail(null); setSpozDetailData(null); setSpozEditData(null); }}
                                    onOpenJednotky={() => setJednotkyModal('SPOZ')} altJednotkyPocet={spozAltJednotky.length}
                                    poleDirty={spozPoleDirty}
                                    isDark={isDark} isMobile={isMobile} mutedColor={mutedColor} secondaryColor={secondaryColor}
                                    optionStyle={optionStyle} btnPrimary={btnPrimary} btnSecondary={btnSecondary} />

                                <JednotkyModal
                                    open={jednotkyModal === 'SPOZ' && !!spozEditData} onClose={() => setJednotkyModal(null)}
                                    isDark={isDark} jednotky={jednotky}
                                    jednotkaId={spozEditData?.jednotka_id || ''}
                                    onJednotkaChange={v => setSpozEditData({ ...spozEditData, jednotka_id: v })}
                                    altJednotky={spozAltJednotky}
                                    onAltJednotkyChange={alt => {
                                        setSpozAltJednotky(alt);
                                        setSpozDetailData(prev => prev ? { ...prev, alt_jednotky: alt } : prev);
                                    }}
                                    api={api}
                                    listUrl={`/api/recepty/spotrebni-zbozi/${spozDetail}/alt-jednotky`}
                                    itemUrl={id => `/api/recepty/alt-jednotky-spotrebni-zbozi/${id}`}
                                    isNew={spozDetail === 'new'} novaHint="Nejdříve uložte zboží." />

                                <div className="flex gap-1 px-4 pt-3" style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                                    {[{ id: 'info', label: 'Základní info' }, { id: 'ceny', label: 'Ceny & dodavatelé' }].map(st => (
                                        <button key={st.id} onClick={() => setSpozDetailTab(st.id)}
                                            className="px-3 py-2 text-xs font-medium"
                                            style={{ color: spozDetailTab === st.id ? SECTION_COLOR : mutedColor, background: 'none', border: 'none',
                                                borderBottom: `2px solid ${spozDetailTab === st.id ? SECTION_COLOR : 'transparent'}`, cursor: 'pointer', marginBottom: -1 }}>
                                            {st.label}
                                        </button>
                                    ))}
                                </div>

                                <div className="p-4 overflow-y-auto" style={{ flex: 1, minHeight: 0 }}>
                                    {/* ─ INFO TAB ─ */}
                                    {spozDetailTab === 'info' && spozEditData && (
                                        <div className="space-y-4">
                                            {/* Název, zařazení, jednotka i aktivita jsou v hlavičce. */}
                                            {renderFormField('Dodavatel', <select value={spozEditData.dodavatel_id || ''} onChange={e => setSpozEditData({ ...spozEditData, dodavatel_id: e.target.value ? parseInt(e.target.value) : null })} style={selectStyle}>
                                                <option value="" style={optionStyle}>—</option>
                                                {dodavatele.map(d => <option key={d.id} value={d.id} style={optionStyle}>{d.nazev}</option>)}
                                            </select>)}
                                            {renderFormField('Poznámka', <textarea value={spozEditData.poznamka || ''} onChange={e => setSpozEditData({ ...spozEditData, poznamka: e.target.value })} rows={3} style={inputStyle} />)}
                                            {spozError && <p className="text-xs" style={{ color: '#EF4444' }}>{spozError}</p>}

                                            {spozDetail !== 'new' && spozDetailData && (
                                                <div className="rounded-xl p-4 mt-2" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-base)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}` }}>
                                                    <p className="text-[11px] font-semibold uppercase tracking-wide mb-3" style={{ color: mutedColor }}>Přehled</p>
                                                    <div className="grid grid-cols-3 gap-4">
                                                        <div>
                                                            <p className="text-[11px]" style={{ color: mutedColor }}>Nákupní cena</p>
                                                            {(() => {
                                                                const pc = spozCeny.find(c => c.je_primarni && (!c.platnost_do || new Date(c.platnost_do) >= new Date()));
                                                                if (!pc) return <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>—</p>;
                                                                const isAlt = pc.cena_jednotka_id && pc.cena_jednotka_id !== spozDetailData.jednotka_id;
                                                                return <>
                                                                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                                                                        {parseFloat(pc.cena_za_jednotku).toFixed(2)} Kč/{spozDetailData.jednotka?.zkratka}
                                                                    </p>
                                                                    {isAlt && <p className="text-[10px]" style={{ color: mutedColor }}>zadáno {parseFloat(pc.cena_bez_dph).toFixed(2)} Kč/{pc.cena_jednotka?.zkratka}</p>}
                                                                </>;
                                                            })()}
                                                        </div>
                                                        <div>
                                                            <p className="text-[11px]" style={{ color: mutedColor }}>Sek. jednotky</p>
                                                            <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{spozAltJednotky.length}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-[11px]" style={{ color: mutedColor }}>V kusovnících</p>
                                                            {/* Klik ukáže, ve kterých recepturách — a nabídne hromadnou náhradu */}
                                                            <button
                                                                onClick={() => spozDetailData.kusovniky_jako_ingredience_count > 0 && setPouzitiModal({ typ: 'SPOZ', id: spozDetailData.id })}
                                                                disabled={!spozDetailData.kusovniky_jako_ingredience_count}
                                                                title={spozDetailData.kusovniky_jako_ingredience_count ? 'Zobrazit, kde se položka používá' : undefined}
                                                                className="text-sm font-semibold"
                                                                style={{
                                                                    background: 'none', border: 'none', padding: 0, textAlign: 'left',
                                                                    cursor: spozDetailData.kusovniky_jako_ingredience_count ? 'pointer' : 'default',
                                                                    color: spozDetailData.kusovniky_jako_ingredience_count > 0 ? SECTION_COLOR : 'var(--text-primary)',
                                                                    textDecoration: spozDetailData.kusovniky_jako_ingredience_count ? 'underline' : 'none',
                                                                }}>
                                                                {spozDetailData.kusovniky_jako_ingredience_count ?? 0}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* ─ CENY TAB ─ */}
                                    {spozDetailTab === 'ceny' && spozDetail !== 'new' && (
                                        <div>
                                            {spozCeny.length > 0 && (
                                                <div className="rounded-xl overflow-hidden mb-3" style={{ border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                                                    <table className="w-full">
                                                        <thead><tr style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                                                            <th style={{ ...thStyle, width: 28 }}></th><th style={thStyle}>Dodavatel</th><th style={thStyle}>Cena/{spozDetailData.jednotka?.zkratka}</th><th style={thStyle}>Zadáno</th><th style={thStyle}>DPH</th>
                                                            <th style={thStyle}>S DPH/{spozDetailData.jednotka?.zkratka}</th><th style={thStyle}>Od</th><th style={thStyle}>Do</th><th style={thStyle}></th>
                                                        </tr></thead>
                                                        <tbody>{spozCeny.map(c => {
                                                            const isAlt = c.cena_jednotka_id && c.cena_jednotka_id !== spozDetailData.jednotka_id;
                                                            return (
                                                            <tr key={c.id} style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'var(--border-default)'}`, fontWeight: c.je_primarni ? 600 : 400 }}>
                                                                <td style={tdStyle}>
                                                                    <button onClick={() => toggleSpozPrimarni(c.id)} title={c.je_primarni ? 'Primární dodavatel' : 'Označit jako primární'}
                                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.je_primarni ? SECTION_COLOR : mutedColor, padding: 0 }}>
                                                                        <Star size={14} fill={c.je_primarni ? SECTION_COLOR : 'none'} />
                                                                    </button>
                                                                </td>
                                                                <td style={tdStyle}>
                                                                    <OdkazNaDodavatele cena={c} />
                                                                    {c.je_primarni && <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: SECTION_BG, color: SECTION_COLOR }}>prim.</span>}
                                                                </td>
                                                                <td style={tdStyle}>{parseFloat(c.cena_za_jednotku).toFixed(2)}</td>
                                                                <td style={{ ...tdStyle, fontSize: 11, color: isAlt ? secondaryColor : mutedColor }}>
                                                                    {isAlt ? `${parseFloat(c.cena_bez_dph).toFixed(2)}/${c.cena_jednotka?.zkratka}` : `/${spozDetailData.jednotka?.zkratka}`}
                                                                </td>
                                                                <td style={tdStyle}>{c.dph_sazba}%</td>
                                                                <td style={tdStyle}>{parseFloat(c.cena_s_dph_za_jednotku).toFixed(2)}</td>
                                                                <td style={tdStyle}>{fmtDate(c.platnost_od)}</td>
                                                                <td style={tdStyle}>{c.platnost_do ? fmtDate(c.platnost_do) : '—'}</td>
                                                                <td style={tdStyle}>
                                                                    <div className="flex gap-1">
                                                                        <button onClick={() => setSpozCenaForm({ ...c, platnost_od: c.platnost_od?.split('T')[0], platnost_do: c.platnost_do?.split('T')[0] || '', je_primarni: c.je_primarni || false })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: secondaryColor }}><Edit3 size={13} /></button>
                                                                        <button onClick={() => deleteSpozCena(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444' }}><Trash2 size={13} /></button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );})}</tbody>
                                                    </table>
                                                </div>
                                            )}
                                            {!spozCenaForm && (
                                                <button onClick={() => setSpozCenaForm({ dodavatel_id: '', cena_bez_dph: '', dph_sazba: 21, cena_jednotka_id: null, platnost_od: new Date().toISOString().split('T')[0], platnost_do: '', je_primarni: spozCeny.length === 0 })} style={btnPrimary}>
                                                    <Plus size={14} /> Přidat cenu
                                                </button>
                                            )}
                                            {spozCenaForm && (
                                                <div className="rounded-xl p-4 space-y-3 mt-2" style={{ border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                                                    {renderFormField('Dodavatel *', <select value={spozCenaForm.dodavatel_id || ''} onChange={e => setSpozCenaForm({ ...spozCenaForm, dodavatel_id: e.target.value ? parseInt(e.target.value) : null })} style={selectStyle}>
                                                        <option value="" style={optionStyle}>—</option>
                                                        {dodavatele.map(d => <option key={d.id} value={d.id} style={optionStyle}>{d.nazev}</option>)}
                                                    </select>)}
                                                    <div className={`grid gap-3 ${spozAltJednotky.length > 0 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                                                        {renderFormField('Cena bez DPH *', <input type="number" step="0.01" value={spozCenaForm.cena_bez_dph} onChange={e => setSpozCenaForm({ ...spozCenaForm, cena_bez_dph: e.target.value })} style={inputStyle} />)}
                                                        {spozAltJednotky.length > 0 && renderFormField('Za jednotku', <select value={spozCenaForm.cena_jednotka_id || ''} onChange={e => setSpozCenaForm({ ...spozCenaForm, cena_jednotka_id: e.target.value ? parseInt(e.target.value) : null })} style={selectStyle}>
                                                            <option value="" style={optionStyle}>{spozDetailData.jednotka?.zkratka} (základní)</option>
                                                            {spozAltJednotky.map(aj => <option key={aj.jednotka_do?.id} value={aj.jednotka_do?.id} style={optionStyle}>{aj.jednotka_do?.zkratka} (1 = {aj.koeficient} {spozDetailData.jednotka?.zkratka})</option>)}
                                                        </select>)}
                                                        {renderFormField('DPH', <select value={spozCenaForm.dph_sazba} onChange={e => setSpozCenaForm({ ...spozCenaForm, dph_sazba: parseInt(e.target.value) })} style={selectStyle}>
                                                            {dphSazby.map(d => <option key={d.sazba} value={d.sazba} style={optionStyle}>{d.nazev}</option>)}
                                                        </select>)}
                                                    </div>
                                                    {(() => {
                                                        const selJed = spozCenaForm.cena_jednotka_id;
                                                        const isAlt = selJed && selJed !== spozDetailData.jednotka_id;
                                                        const alt = isAlt && spozAltJednotky.find(a => a.jednotka_do?.id === selJed);
                                                        const cenaBez = parseFloat(spozCenaForm.cena_bez_dph);
                                                        if (isAlt && alt && alt.koeficient > 0 && cenaBez > 0) {
                                                            const zaZakl = cenaBez / alt.koeficient;
                                                            const sDph = zaZakl * (1 + spozCenaForm.dph_sazba / 100);
                                                            return <p className="text-xs px-1 py-1.5 rounded" style={{ background: isDark ? 'rgba(245,158,11,0.08)' : 'rgba(245,158,11,0.06)', color: SECTION_COLOR }}>
                                                                = {zaZakl.toFixed(2)} Kč/{spozDetailData.jednotka?.zkratka} bez DPH | {sDph.toFixed(2)} Kč/{spozDetailData.jednotka?.zkratka} s DPH
                                                            </p>;
                                                        }
                                                        return null;
                                                    })()}
                                                    <div className="grid grid-cols-3 gap-3 items-end">
                                                        {renderFormField('Platnost od *', <input type="date" value={spozCenaForm.platnost_od || ''} onChange={e => setSpozCenaForm({ ...spozCenaForm, platnost_od: e.target.value })} style={inputStyle} />)}
                                                        {renderFormField('Platnost do', <input type="date" value={spozCenaForm.platnost_do || ''} onChange={e => setSpozCenaForm({ ...spozCenaForm, platnost_do: e.target.value || null })} style={inputStyle} />)}
                                                        <label className="flex items-center gap-2 text-xs pb-1" style={{ color: 'var(--text-primary)', cursor: 'pointer' }}>
                                                            <input type="checkbox" checked={spozCenaForm.je_primarni || false} onChange={e => setSpozCenaForm({ ...spozCenaForm, je_primarni: e.target.checked })} />
                                                            <Star size={12} style={{ color: SECTION_COLOR }} /> Primární
                                                        </label>
                                                    </div>
                                                    <div className="flex justify-end gap-2">
                                                        <button onClick={() => setSpozCenaForm(null)} style={btnSecondary}>Zrušit</button>
                                                        <button onClick={saveSpozCena} style={btnPrimary} disabled={spozCenaSaving}>
                                                            {spozCenaSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Uložit
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {spozDetailTab === 'ceny' && spozDetail === 'new' && (
                                        <p className="text-xs py-4" style={{ color: mutedColor }}>Nejdříve uložte zboží.</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── Statistika: cena, vývoj a kam vstupuje ── */}
                    {!isMobile && isWide && spozDetail && spozDetail !== 'new' && (
                        <div style={{ width: 300, flexShrink: 0, minHeight: 0, overflowY: 'auto' }}>
                            <MaterialStatistika
                                typ="SPOZ" detail={spozDetailData} ceny={spozCeny}
                                isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor}
                                cardStyle={cardStyle} sekceBarva={SEKCE}
                                onPouziti={() => setPouzitiModal({ typ: 'SPOZ', id: spozDetail })}
                            />
                        </div>
                    )}
                </div>
            )}

            {pouzitiModal && (
                <PouzitiModal
                    typ={pouzitiModal.typ}
                    id={pouzitiModal.id}
                    isDark={isDark}
                    onNavigate={onNavigate}
                    onClose={() => setPouzitiModal(null)}
                    onZmena={() => {
                        // Náhrada přepsala kusovníky — počty i ceny v seznamu jsou po ní jiné.
                        if (tab === 'suroviny') { loadSuroviny(); if (surDetail && surDetail !== 'new') loadSurDetail(surDetail); }
                        if (tab === 'obaly') {
                            loadObaly(); loadSpoz();
                            if (obalDetail && obalDetail !== 'new') loadObalDetail(obalDetail);
                            if (spozDetail && spozDetail !== 'new') loadSpozDetail(spozDetail);
                        }
                    }}
                />
            )}

        </div>
    );
}
